import React, { useState } from "react";

import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuLabel } from "~/components/dropdown-menu/label";
import { DropdownMenuSeparator } from "~/components/dropdown-menu/separator";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";

import type { BorgContextPin } from "../models/borg-types";

import { formatPinLabel } from "./_format-pin-label";
import { pinKey } from "./_use-context-bar-state";

interface ContextPickerProps {
	onAddPin: (pin: BorgContextPin) => void;
	removedPins: ReadonlyArray<BorgContextPin>;
}

export const ContextPicker: React.FC<ContextPickerProps> = ({ onAddPin, removedPins }) => {
	const [noteText, setNoteText] = useState("");
	const [open, setOpen] = useState(false);

	const handleAddNote = () => {
		const trimmed = noteText.trim();
		if (trimmed.length === 0) {
			return;
		}
		onAddPin({ type: "filter", name: "note", value: trimmed });
		setNoteText("");
		setOpen(false);
	};

	return (
		<DropdownMenu onOpenChange={setOpen} open={open}>
			<DropdownMenuTrigger>
				<button
					aria-label="Add context"
					className="cursor-pointer rounded-full border border-gray-400 border-dashed bg-gray-100 px-2.5 py-1 text-gray-600 text-xs transition-colors hover:border-primary-500 hover:bg-white hover:text-primary-600"
					type="button"
				>
					+
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" side="top">
				<DropdownMenuLabel>Add a note</DropdownMenuLabel>
				<div className="flex gap-1 px-2 pb-1">
					<input
						className="min-w-0 flex-1 rounded-md border border-gray-400 px-2.5 py-1.5 text-gray-900 text-xs focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
						onChange={(e) => setNoteText(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleAddNote();
							}
						}}
						placeholder="e.g. Deployed v2.3"
						type="text"
						value={noteText}
					/>
					<button
						className="cursor-pointer rounded-md border border-primary-600 bg-primary-600 px-2.5 py-1.5 text-white text-xs transition-colors hover:border-primary-500 hover:bg-primary-500 disabled:opacity-40"
						disabled={noteText.trim().length === 0}
						onClick={handleAddNote}
						type="button"
					>
						Add
					</button>
				</div>
				{removedPins.length > 0 && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuLabel>Removed context</DropdownMenuLabel>
						{removedPins.map((pin) => (
							<DropdownMenuItem key={pinKey(pin)} onSelect={() => onAddPin(pin)}>
								{formatPinLabel(pin)}
							</DropdownMenuItem>
						))}
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
